import { useCallback, useEffect, useState } from 'react';
import { getReviewQueue, updateFinanceTransaction, type ReviewSuggestion, type ReviewTransaction } from '../api.js';
import { Button, EmptyState, ErrorState, useToast } from '../ui/index.js';
import { CategoryPickerBody, type PickResult } from './CategoryPicker.js';
import { dayLabel, money } from './format.js';
import { SkeletonGroup } from './parts.js';
import { useCategoryTree, type CategoryIndex } from './tree.js';

/**
 * Review — the uncategorized rows, one at a time, picker inline. Remembering a
 * merchant files its other rows too, so a queue of 80 often clears in 20 taps.
 * Each pick gets an Undo toast that puts that one row back in the queue; the
 * rule "Remember" wrote, and the rows it filed, stay.
 *
 * Each row arrives with up to three suggestions (a rule, an amount band, or
 * this merchant's history — server-side and deterministic, no model). They
 * show as chips above the picker: tap one, or press 1/2/3, or Enter for the
 * first. A chip files through the picker's own pick, so "Remember" applies.
 */
export default function Review({ onCount }: { onCount?: (n: number) => void }): JSX.Element {
  const [queue, setQueue] = useState<ReviewTransaction[]>([]);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filed, setFiled] = useState<string | null>(null);
  const toast = useToast();
  const cats = useCategoryTree();

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await getReviewQueue();
      setQueue(res.transactions);
      // An empty slice means nothing is left, whatever the count says (and stops a refetch loop).
      setTotal(res.transactions.length === 0 ? 0 : res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!loading) onCount?.(total);
  }, [total, loading, onCount]);

  // The page holds a slice of the queue; when it runs dry with rows still left, fetch the next slice.
  useEffect(() => {
    if (!loading && queue.length === 0 && total > 0) void load();
  }, [loading, queue.length, total, load]);

  const current = queue[0];

  const onPicked = (r: PickResult): void => {
    if (!current) return;
    const before = current;
    const name = (current.merchant ?? '').toLowerCase();
    // The server filed this merchant's other uncategorized rows; drop them here too.
    const rest = queue.slice(1).filter((t) => !(r.alsoFiled > 0 && name && (t.merchant ?? '').toLowerCase() === name));
    setQueue(rest);
    setTotal((n) => Math.max(0, n - 1 - r.alsoFiled));
    setDone((n) => n + 1 + r.alsoFiled);
    setFiled(r.alsoFiled > 0 ? `Filed ${r.alsoFiled} more from ${current.merchant ?? current.description}` : null);
    toast.undoable({
      text: `Filed ${before.merchant ?? before.description} as ${cats.full(r.category)}`,
      undo: () =>
        updateFinanceTransaction(before.id, { category: before.category ?? 'uncategorized', isTransfer: before.isTransfer }),
      onUndone: () => {
        setFiled(null);
        setQueue((q) => [before, ...q.filter((t) => t.id !== before.id)]);
        setTotal((n) => n + 1);
        setDone((n) => Math.max(0, n - 1));
      },
    });
  };

  const skip = (): void => {
    if (queue.length < 2) return;
    setFiled(null);
    setQueue((q) => [...q.slice(1), q[0]!]);
  };

  if (loading) return <SkeletonGroup />;
  if (error) return <ErrorState message={error} onRetry={() => void load()} />;
  if (!current) {
    return (
      <>
        {filed && <p className="mn-filed" role="status">{filed}</p>}
        <EmptyState icon="check" title="All sorted" hint={done > 0 ? `${done} filed this session.` : 'Every transaction has a category.'} />
      </>
    );
  }

  const queueCents = queue.reduce((s, t) => s + Math.abs(t.amountCents), 0);
  const inflow = current.amountCents > 0;

  return (
    <div className="mn-review">
      <div className="mn-review__head">
        <span className="mn-review__count">
          <b className="num">{total}</b> to sort · <span className="num">{money(queueCents)}</span>
        </span>
        {done > 0 && <span className="mn-review__done">{done} done</span>}
      </div>
      <span className="hb-bar__track mn-review__bar" aria-hidden="true">
        <span className="hb-bar__fill" style={{ width: `${(done / Math.max(1, done + total)) * 100}%` }} />
      </span>

      {filed && (
        <p className="mn-filed" role="status">
          {filed}
        </p>
      )}

      <section className="ui-card mn-review__card" aria-label="Transaction to sort">
        <div className="mn-review__txn">
          <span className="mn-review__merchant">{current.merchant ?? current.description}</span>
          <span className={`mn-review__amt num${inflow ? ' hb-amount--in' : ''}`}>{money(current.amountCents, { sign: inflow })}</span>
          <span className="mn-review__meta">
            {dayLabel(current.postedAt.slice(0, 10))}
            {current.pending && ' · pending'}
            {current.merchant && current.description !== current.merchant ? ` · ${current.description}` : ''}
          </span>
        </div>
        <CategoryPickerBody
          key={current.id}
          txn={current}
          inline
          onPicked={onPicked}
          lead={(pick, busy) =>
            current.suggestions && current.suggestions.length > 0 ? (
              <Suggestions items={current.suggestions} cats={cats} pick={pick} busy={busy} />
            ) : null
          }
        />
        <div className="mn-review__actions">
          <Button variant="secondary" onClick={skip} disabled={queue.length < 2}>
            Skip
          </Button>
        </div>
      </section>
    </div>
  );
}

/** True when a key press belongs to a text field, not to the Review shortcuts. */
function typing(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('input, textarea, select, [contenteditable="true"]') !== null;
}

/** The suggestion chips: label + why. 1/2/3 pick that chip, Enter the first. */
function Suggestions({
  items,
  cats,
  pick,
  busy,
}: {
  items: ReviewSuggestion[];
  cats: CategoryIndex;
  pick: (path: string) => void;
  busy: boolean;
}): JSX.Element {
  const shown = items.slice(0, 3);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (busy || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || typing(e.target)) return;
      let i = -1;
      if (e.key === '1' || e.key === '2' || e.key === '3') i = Number(e.key) - 1;
      // Enter on a focused button already clicks that button.
      else if (e.key === 'Enter' && !(e.target instanceof Element && e.target.closest('button, a'))) i = 0;
      const s = shown[i];
      if (!s) return;
      e.preventDefault();
      pick(s.category);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shown, pick, busy]);

  return (
    <div className="mn-suggest" role="group" aria-label="Suggested categories">
      {shown.map((s, i) => {
        const label = cats.byPath.has(s.category) ? cats.full(s.category) : s.label;
        return (
          <button
            key={s.category}
            type="button"
            className={`mn-suggest__chip mn-suggest__chip--${s.confidence}`}
            aria-label={`${label} — ${s.reason}`}
            aria-keyshortcuts={i === 0 ? '1 Enter' : String(i + 1)}
            disabled={busy}
            onClick={() => pick(s.category)}
          >
            <kbd className="mn-suggest__key" aria-hidden="true">
              {i + 1}
            </kbd>
            <span className="mn-suggest__text">
              <span className="mn-suggest__label">{label}</span>
              <span className="mn-suggest__why">{s.reason}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
